/**
 * Parametric PDF print mutation for 患者情報検索.
 * Wraps 3 service fns behind one `type` param. Opens the result PDF in a tab
 * named `patient_pdf` so all 3 print buttons (一覧/はがき/ラベル) reuse the
 * same tab — clicking another button overwrites the existing PDF view.
 */

/** Shared window name — all 3 print buttons reuse this single tab. */
const PDF_TAB_NAME = 'patient_pdf';
const PDF_LOADING_HTML =
  `<html><head><title>PDFを準備しています…</title></head>` +
  `<body style="margin:0;display:flex;align-items:center;justify-content:center;` +
  `height:100vh;font-family:sans-serif;color:#555;background:#f7f7f7">` +
  `PDFを準備しています…</body></html>`;

import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import {
  printSearchPatientLabelPdf,
  printSearchPatientListPdf,
  printSearchPatientPostcardPdf,
} from '@/services/main/searchPatientList';
import {
  isServerError,
  showServerErrorModal,
} from '@/pages/main/searchPatientList/serverErrorModal';
import type {
  PrintSearchPatientPayload,
  PrintSearchPatientType,
} from '@/pages/main/searchPatientList/types';

/** Decode a Blob error body (axios returns it as Blob when responseType=blob). */
async function readErrorBlob(err: unknown): Promise<string | null> {
  if (!axios.isAxiosError(err)) return null;
  const data = err.response?.data;
  if (data instanceof Blob) {
    try {
      return await data.text();
    } catch {
      return null;
    }
  }
  return typeof data === 'string' ? data : null;
}

function resolvePrinter(type: PrintSearchPatientType) {
  switch (type) {
    case 'list':
      return printSearchPatientListPdf;
    case 'postcard':
      return printSearchPatientPostcardPdf;
    case 'label':
      return printSearchPatientLabelPdf;
  }
}

function showPdfLoadingState(tab: Window | null): string | null {
  if (!tab) return null;

  try {
    tab.document.open();
    tab.document.write(PDF_LOADING_HTML);
    tab.document.close();
    return null;
  } catch {
    // If the named tab currently displays a browser PDF viewer, document.write
    // may be blocked. Navigate it to a temporary same-origin loading page instead.
    const loadingUrl = URL.createObjectURL(new Blob([PDF_LOADING_HTML], { type: 'text/html' }));
    try {
      tab.location.replace(loadingUrl);
      return loadingUrl;
    } catch {
      URL.revokeObjectURL(loadingUrl);
      return null;
    }
  }
}

export interface UsePrintSearchPatientOptions {
  type: PrintSearchPatientType;
}

export function usePrintSearchPatientMutation(options: UsePrintSearchPatientOptions) {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();
  const appModal = useAppModal();
  const printer = resolvePrinter(options.type);

  const { mutateAsync, isPending: isLoading } = useMutation<
    Blob,
    Error,
    PrintSearchPatientPayload
  >({
    mutationFn: payload => printer(tenant, clinicNumber, payload),
  });

  const triggerPrint = async (payload: PrintSearchPatientPayload) => {
    // Open tab BEFORE await so Safari/Chromium don't treat it as popup.
    // Named tab → reused by all 3 print buttons (legacy: target="patient_pdf").
    const tab = window.open('', PDF_TAB_NAME);
    const loadingUrl = showPdfLoadingState(tab);
    try {
      const blob = await mutateAsync(payload);

      // Defensive: if server returned 200 with JSON body (shouldn't happen), surface error.
      if (blob.type.includes('json')) {
        const text = await blob.text();
        // eslint-disable-next-line no-console
        console.error('[SearchPatient print] unexpected JSON response:', text);
        tab?.close();
        if (loadingUrl) URL.revokeObjectURL(loadingUrl);
        toast.error('印刷に失敗しました。');
        return;
      }

      const url = URL.createObjectURL(blob);
      if (tab) {
        tab.location.replace(url);
      } else {
        window.open(url, PDF_TAB_NAME);
      }
      if (loadingUrl) URL.revokeObjectURL(loadingUrl);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      tab?.close();
      if (loadingUrl) URL.revokeObjectURL(loadingUrl);
      const body = await readErrorBlob(err);
      // eslint-disable-next-line no-console
      console.error('[SearchPatient print] failed:', body ?? err);
      if (isServerError(err)) {
        showServerErrorModal(appModal.warning);
        return;
      }
      toast.error('印刷に失敗しました。');
    }
  };

  return { triggerPrint, isLoading };
}
